// src/composables/useMapbox.js
import { ref, onMounted, onUnmounted, watch } from 'vue';
import mapboxgl from 'mapbox-gl';

export function useMapbox(containerRef, options = {}) {
  let map;                    // ← plain variable
  const isReady = ref(false);

  function initMap() {
    if (!containerRef.value || map) return;
    mapboxgl.accessToken = options.accessToken || mapboxgl.accessToken;
    map = new mapboxgl.Map({
      container: containerRef.value,
      style:     options.style  || 'mapbox://styles/mapbox/streets-v11',
      center:    options.center || [0, 0],
      zoom:      options.zoom   ?? 12,
    });

    map.on('load', () => {
      isReady.value = true;
      options.onLoad?.(map);
    });

    options.onMove && map.on('move', options.onMove);
    options.onMoveEnd && map.on('moveend', options.onMoveEnd);
    options.onClick   && map.on('click',    options.onClick);
    options.onZoom    && map.on('zoom',     options.onZoom);
  }

  function destroyMap() {
    if (map) {
      map.remove();
      map = null;
      isReady.value = false;
    }
  }

  watch(containerRef, el => el ? initMap() : destroyMap());
  onMounted(initMap);
  onUnmounted(destroyMap);

  // Expose a getter so consuming code can grab the map instance once ready:
  function getMap() {
    return map;
  }

  // Function to change map style
  function setMapStyle(styleUrl) {
    if (map) {
      console.log('Changing map style to:', styleUrl);
      map.setStyle(styleUrl);
      
      // Re-add custom sources and layers after style loads
      map.once('styledata', () => {
        console.log('Style loaded, triggering onLoad callback to re-add layers');
        if (options.onLoad) {
          options.onLoad(map);
        }
      });
    } else {
      console.warn('Map not initialized yet, cannot change style');
    }
  }

  return { isReady, getMap, setMapStyle };
}
